import { trackFunnelStep, trackError } from '~/utils/monitoring';
import '~/components/breadcrumb/breadcrumb';
import '~/components/accommodation-title/accommodation-title';
import '~/components/accommodation-gallery/gallery';
import '~/components/accommodation-info/accommodation-info';
import '~/components/accommodation-description/accommodation-description';
import '~/components/facilities-grid/facilities-grid';
import '~/components/facilities-list/facilities-list';
import '~/components/quick-links-nav/quick-links-nav';
import '~/components/trip-dates-overview/trip-dates-overview';
import '~/components/accommodations-reviews/accommodations-reviews';
import '~/components/date-selector/date-selector';
import { DateSelectedEventDetail, DateSelector } from '~/components/date-selector/date-selector';
import '~/components/start-booking-form/start-booking-form';
import { StartBookingForm, StartBookingFormEvent } from '~/components/start-booking-form/start-booking-form';
import '~/components/button/button';
import { Button } from '~/components/button/button';
import '~/components/party-selector/party-selector';
import '~/components/park-card/park-card';
import '~/components/error-notice/error-notice';
import '~/components/breadcrumb/breadcrumb';

import { Party } from '~/types/Party';
import { PriceAvailability } from '~/types/PriceAvailability';
import { DVGStateManager } from '~/assets/js/state';
import { DVGFetcher } from '~/assets/js/fetcher';
import { DVGCacheManager } from '~/assets/js/cache';
import { PriceOption } from '~/components/booking-receipt/booking-receipt';
import { MinimalHousing } from '~/types/Housing';
import { getDuration } from '~/utils/utils';
import { ApiError, ApiRateLimitError } from '~/types/ApiError';
import { AvailabilityStatus } from '~/components/start-booking-form/start-booking-form';
import { ErrorNotice } from '~/components/error-notice/error-notice';

type BookingsDetails = {
  startDate: string | undefined;
  endDate: string | undefined;
  duration: number | undefined;
  priceAvailability?: PriceAvailability;
  party: Party | undefined;
}

class DvgAccommodationDetails {
  stateManager = new DVGStateManager();
  cacheManager = new DVGCacheManager();
  fetcher = new DVGFetcher();
  controller: AbortController = new AbortController();

  ctaButton = document.querySelector<Button>('.booking-cta-button');
  detailsPage = document.querySelector('.dvg-accommodation-details');

  dateSelector = document.querySelector<DateSelector>('#date-selector');
  bookingForm = document.querySelector<StartBookingForm>('#start-booking-form');

  errorNotice = document.querySelector<ErrorNotice>('#accommodation-error-notice');

  bookingsDetails: BookingsDetails = {
    startDate: undefined,
    endDate: undefined,
    duration: undefined,
    party: undefined,
  };

  bookingCachePrefilled: boolean = false;

  private availabilities: PriceAvailability[] | null = null;

  AVAILABILITY_TIMEOUT = 5_000;
  availabilityFetchTimeoutID: number | undefined = undefined;

  constructor() {
    this.clearBookingCache();
    this.fetchAvailabilities();
    this.prefillBookingCache();
    this.initEventListeners();
    this.determineMinimumAvailablePrice();
  }

  private async fetchAvailabilities(): Promise<void> {
    const slug = this.stateManager.getAccommodationSlug();
    if (!slug) return;

    this.availabilityFetchTimeoutID = window.setTimeout(() => {
      console.error(`[Availabilities] Fetching availabilities is taking longer than expected (${this.AVAILABILITY_TIMEOUT} ms). Aborting request.`);
      this.controller.abort();
    }, this.AVAILABILITY_TIMEOUT);

    try {
      this.availabilities = await this.fetcher.fetchAvailabilities(slug, {
        signal: this.controller.signal,
      });

      window.clearTimeout(this.availabilityFetchTimeoutID);

      if (this.dateSelector) {
        this.dateSelector.availability = this.availabilities;
      }
      if (this.bookingForm) {
        this.bookingForm.availability = this.availabilities;
      }
    } catch (error) {
      window.clearTimeout(this.availabilityFetchTimeoutID);
      // Ignore aborts triggered by page navigation
      if (error instanceof DOMException && error.name === 'AbortError') {
        if (this.errorNotice && this.bookingForm) {
          this.errorNotice.heading = "Er kan op dit moment geen beschikbaarheid worden opgehaald. Probeer het later opnieuw of neem contact met ons op.";
          this.errorNotice.show = true;
        }
        return;
      }

      console.error('[Availabilities] Failed to fetch availabilities:', error);
      // Pass empty array so both components exit the loading state gracefully
      if (this.dateSelector) this.dateSelector.availability = [];
      if (this.bookingForm) this.bookingForm.availability = [];
    }
  }

  initEventListeners() {
    this.ctaButton?.addEventListener('click', () => {
      this.showDateSelector();
    });

    this.bookingForm?.addEventListener('dvg:booking-start', (event: Event) => {
      this.handleStartBookingEvent(event as CustomEvent);
    });

    this.bookingForm?.addEventListener('dvg:booking-alternate-dates', (event: Event) => {
      this.showDateSelector();
    });

    window.addEventListener('dvg:date-selected', (event) => {
      this.handleDateSelectionChange(event as CustomEvent<DateSelectedEventDetail>);
    });

    window.addEventListener('dvg:date-cleared', () => {
      this.handleDateCleared();
    });

    window.addEventListener('dvg:party-change', (event) => {
      const party = (event as CustomEvent<{ party: Party }>).detail.party;
      this.handleBookingDetailsUpdate({ party });
    });

    window.addEventListener('beforeunload', () => {
      this.controller.abort();
    });
  }

  determineMinimumAvailablePrice(): void {
    if (!this.bookingForm) return;

    const { durationId } = this.stateManager.getArrivalAndDepartureFromURL();
    if (durationId) {
      const priceAvailability = this.availabilities?.find(avail => avail.id === parseInt(durationId));
      if (priceAvailability) {
        const { price, distribution_price } = priceAvailability;
        if (distribution_price && distribution_price < price) {
          this.bookingForm.priceLabel = 'Vanaf';
          this.bookingForm.price = parseFloat(distribution_price);
          this.bookingForm.discountedPrice = parseFloat(price);
        } else if (price) {
          this.bookingForm.priceLabel = 'Vanaf';
          this.bookingForm.price = parseFloat(price);
        } else {
          this.bookingForm.priceLabel = undefined;
          this.bookingForm.price = undefined;
        }
      }
    } else {
      const minimumPrice = this.detailsPage?.getAttribute('data-minimum-price');
      if (minimumPrice) {
        this.bookingForm.priceLabel = 'Vanaf';
        this.bookingForm.price = parseFloat(minimumPrice);
      } else {
        this.bookingForm.priceLabel = undefined;
        this.bookingForm.price = undefined;
      }
    }
  }

  clearBookingCache() {
    const accommodationId = this.stateManager.getAccommodationId();
    this.cacheManager.fullyClearCache(accommodationId);
  }

  private setAvailabilityStatus(status: AvailabilityStatus | undefined) {
    if (this.bookingForm) {
      this.bookingForm.availabilityStatus = status;
    }
  }

  async prefillBookingCache() {
    if (this.bookingCachePrefilled) {
      console.log('[Booking Cache] Booking cache already prefilled, skipping prefill.');
      return;
    }

    const { arrival, departure } = this.stateManager.getArrivalAndDepartureFromURL();
    const accommodationId = this.stateManager.getAccommodationId();

    if (!arrival || !departure || !accommodationId) {
      console.warn('[Booking Cache] Insufficient data to prefill booking cache.');
      return;
    }

    const duration = getDuration(departure, arrival);

    const combinedSignal = AbortSignal.any([
      this.controller.signal,
      AbortSignal.timeout(10_000),
    ]);

    performance.mark('prefill-booking-cache-start');

    const party: Party = { adults: 2, children: 0, infants: 0, pets: 0 };

    try {
      console.log('[Booking Cache] Prefilling booking cache for accommodation:', accommodationId);
      console.log('[Booking Cache] Fetching additional options for prefill...');
      this.setAvailabilityStatus('checking');

      const bookableCompsResp = await this.fetcher.fetchAdditionalOptions({
        accommodationId,
        startDate: arrival,
        duration,
        party,
      }, {
        signal: combinedSignal,
      })
        .catch((error) => {
          console.error('[Booking Cache] Error fetching additional options for prefill:', error);
          trackError(error, { step: 'accommodation_viewed', trigger: 'prefetch_options' });
          if ('errorMessage' in error) {
            if (error.errorMessage === 'Huisvesting niet beschikbaar. (Aankomst aankomst is in het verleden of de receptie is al gesloten)') {
              this.setAvailabilityStatus('unavailable');
            }
          }
          this.setAvailabilityStatus(undefined);
        });

      if (!bookableCompsResp) {
        console.warn('[Booking Cache] No additional options data received for prefill.');
        return;
      }

      console.log('[Booking Cache] Successfully fetched additional options for prefill, processing data...');
      const { extras, housing, trackingId } = bookableCompsResp;
      this.cacheManager.storeAdditionalOptions(accommodationId, extras);
      console.log('[Booking Cache] Additional options cached successfully for accommodation:', accommodationId);

      console.log('[Booking Cache] Fetching booking check for prefill...');

      let bookingResponse;
      try {
        bookingResponse = await this.fetcher.checkOrCreateBooking({
          mode: 'check',
          accommodationId,
          startDate: arrival,
          duration,
          party,
          housing,
        }, {
          signal: combinedSignal,
        });
      } catch (error) {
        console.error('[Booking Cache] Error checking booking for prefill:', error);
        // Rate limit → check-failed (temporary, user can retry)
        // ApiError means Qenner explicitly rejected it (e.g. accommodation booked)
        // Other errors are network/timeout — don't block the user
        if (error instanceof ApiRateLimitError) {
          this.setAvailabilityStatus('check-failed');
        } else if (error instanceof ApiError) {
          this.setAvailabilityStatus('unavailable');
        } else {
          this.setAvailabilityStatus('check-failed');
        }
        return;
      }

      if (!bookingResponse) {
        console.warn('[Booking Cache] No booking check data received for prefill.');
        this.setAvailabilityStatus('check-failed');
        return;
      }

      console.log('[Booking Cache] Successfully fetched booking check for prefill, processing data...');
      this.setAvailabilityStatus('available');

      const { priceInfo } = bookingResponse;
      const requiredOptions: PriceOption[] = priceInfo.priceDetails.map(detail => ({
        id: detail.category,
        amount: detail.quantity,
        unitPrice: detail.unitPrice,
        description: detail.description,
        totalPrice: detail.totalPrice,
        selectedByUser: false,
      }));
      if (this.bookingForm) {
        this.bookingForm.price = priceInfo.totalPrice;
      }

      const housingToCache: MinimalHousing = {
        unitId: housing[0].unitId,
        unitCode: housing[0].unitCode ?? housing[0].unitId,
        code: housing[0].code,
        boardCode: housing[0].boardCode ?? housing[0].boards[0].code,
      };

      this.cacheManager.storeBookingData(accommodationId, priceInfo.totalPrice, requiredOptions, housingToCache, trackingId);
      this.bookingCachePrefilled = true;

      performance.mark('prefill-booking-cache-end');
      const cacheMeasurement = performance.measure(
        'Prefill Booking Cache Duration',
        'prefill-booking-cache-start',
        'prefill-booking-cache-end'
      );

      console.log('[Booking Cache] Successfully prefetched and cached booking data for accommodation:', accommodationId);
      console.log(`[Booking Cache] Prefilling booking cache took ${cacheMeasurement.duration.toFixed(2)} ms.`);
    } catch (error) {
      console.error('[Booking Cache] Error during prefill booking cache:', error);
      this.setAvailabilityStatus('check-failed');
    }
  }

  showDateSelector() {
    this.bookingForm?.scrollIntoView({ behavior: 'smooth' });
  }

  handleBookingDetailsUpdate(details: Partial<BookingsDetails>) {
    this.bookingsDetails = { ...this.bookingsDetails, ...details };

    // TODO: We can remove the priceAvailability from the URL because we only need duration which we can calculate
    this.stateManager.storeArrivalAndDepartureInURL(
      this.bookingsDetails.startDate || '',
      this.bookingsDetails.endDate || '',
      this.bookingsDetails.priceAvailability?.id?.toString() || '',
    );

    const party = this.bookingsDetails.party;
    if (party && this.validatePartyDetails(party)) {
      this.stateManager.storePartyInURL({
        adults: party?.adults || 0,
        children: party?.children || 0,
        infants: party?.infants || 0,
        pets: party?.pets || 0,
      });
    }

    this.updateDateSelection();

    if (party?.adults) {
      this.prefillBookingCache();
    }
  }

  private validatePartyDetails = (party: Party): boolean => {
    if (this.bookingForm) {
      this.bookingForm.errorMessage = '';
      this.bookingForm.fieldHasError = undefined;
      this.bookingForm.ariaInvalid = null;
    }

    if (party.adults === 0 && party.children + party.infants > 0) {
      if (this.bookingForm) {
        this.bookingForm.ariaInvalid = 'true';
        this.bookingForm.errorMessage = 'Er moet minimaal 1 volwassene aanwezig zijn.';
        this.bookingForm.fieldHasError = 'party';
      }
      return false;
    }

    return true;
  }

  handleDateSelectionChange(event: CustomEvent<DateSelectedEventDetail>) {
    const { startDate, endDate, duration, priceAvailability } = event.detail.selectedDates;
    this.setAvailabilityStatus(undefined);
    this.bookingCachePrefilled = false;
    this.handleBookingDetailsUpdate({ startDate, endDate, duration, priceAvailability });

    if (startDate && endDate) {
      this.prefillBookingCache();
    }
  }

  handleDateCleared() {
    this.setAvailabilityStatus(undefined);
    this.bookingCachePrefilled = false;
    this.handleBookingDetailsUpdate({
      startDate: undefined,
      endDate: undefined,
      duration: undefined,
      priceAvailability: undefined,
    });
  }

  handleStartBookingEvent(event: CustomEvent<StartBookingFormEvent>) {
    if (this.bookingForm) {
      this.bookingForm.bookingHasStarted = true;
    }
    const { startDate, endDate, duration, party, priceAvailability } = event.detail;
    this.handleBookingDetailsUpdate({ startDate, endDate, duration, party, priceAvailability });

    const slug = this.stateManager.getAccommodationSlug();

    this.stateManager.navigate(`/vakantiehuisjes/${slug}/boeken`);
  }

  updateDateSelection() {
    if (this.bookingsDetails.startDate && this.bookingsDetails.endDate) {
      if (this.dateSelector) {
        this.dateSelector.startDate = this.bookingsDetails.startDate;
        this.dateSelector.endDate = this.bookingsDetails.endDate;
      }

      if (this.bookingForm) {
        this.bookingForm.startDate = this.bookingsDetails.startDate;
        this.bookingForm.endDate = this.bookingsDetails.endDate;
      }
    }

    if (this.bookingsDetails.party) {
      if (this.bookingForm) {
        this.bookingForm.party = this.bookingsDetails.party;
      }
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const slug = document.querySelector<HTMLElement>('[data-accommodation-slug]')?.dataset.accommodationSlug;
  trackFunnelStep('accommodation_viewed', slug ? { accommodation_slug: slug } : {});
  new DvgAccommodationDetails();
});
